import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import * as lodash from 'lodash'
import { ShareContractDialogService } from './dialogs/share/share-contract-dialog.service'
import { RenameContractDialogService } from './dialogs/rename/rename-contract-dialog.service'
import { AssignInquiryDialogService } from './dialogs/assign/assign-inquiry-dialog.service'
import {
  ContractService,
  ContractView,
  CancelContract,
  SendContract,
  MarkContractAsCompleted,
  SyncContract,
  ContractSigner,
  DeleteContract,
  SignatureFileService,
} from '@tokeet-frontend/signature'
import { ConfirmDialogService, ActionFailed } from '@tokeet-frontend/tv3-platform'
import { switchMap } from 'rxjs/operators'
import { of } from 'rxjs'
import { SendContractService } from '../templates/send-drawer/send-contract.service'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'

@Injectable()
export class ContractHelperService {
  constructor(
    private store: Store<any>,
    private fileService: SignatureFileService,
    private contractService: ContractService,
    private inquiryService: InquiryService,
    private confirmDialog: ConfirmDialogService,
    private shareContractDialog: ShareContractDialogService,
    private renameContractDialog: RenameContractDialogService,
    private assignInquiryDialog: AssignInquiryDialogService,
    private sendContractService: SendContractService
  ) {}

  onDownloadContract(item: ContractView) {
    this.fileService.downloadToLocal(item.pdf_file_url, `${item.name}.pdf`).subscribe()
  }

  onCancel(item: ContractView) {
    this.confirmDialog
      .confirm({
        title: $localize`Cancel?`,
        body: $localize`Are you sure that you want to cancel this document?`,
      })
      .subscribe(() => {
        this.store.dispatch(CancelContract({ id: item.id }))
      })
  }

  onResend(item: ContractView) {
    this.store.dispatch(SendContract({ id: item.id }))
  }

  onComplete(item: ContractView) {
    this.confirmDialog
      .confirm({
        title: $localize`Mark as Completed`,
        body: $localize`Are you sure that you want to mark this document as completed?`,
      })
      .subscribe(() => {
        this.store.dispatch(MarkContractAsCompleted({ id: item.id }))
      })
  }

  onSync(item: ContractView) {
    item = lodash.cloneDeep(item)
    const inquiryId = item.attributes?.inquiry_id
    this.confirmDialog
      .confirm({
        title: $localize`Update Contract`,
        body: $localize`Updating the document will resync all data tokens in the document. If there have been any updates made to the contact, booking, or rental details, then the document will update to show them. The signer will see the updated contents the next time they open the document.`,
      })
      .pipe(
        switchMap(() => (inquiryId ? this.inquiryService.load(inquiryId) : of(null))),
        switchMap((inquiry) => this.sendContractService.tryToFillWithTokeetData(item, inquiry))
      )
      .subscribe(() => {
        this.store.dispatch(SyncContract({ id: item.id, payload: { fields: item.fields } }))
      })
  }

  onShare(contract: ContractView) {
    this.shareContractDialog.open(contract)
  }

  onRename(contract: ContractView) {
    this.renameContractDialog.open(contract)
  }

  onAssignInquiry(contract: ContractView) {
    return this.assignInquiryDialog.open(contract)
  }

  generateLink(contract: ContractView, signer: ContractSigner, cb: (link: string) => void) {
    this.contractService.generateSignLink(contract.id, signer.role).subscribe(
      (link) => {
        cb(link)
      },
      (error) => {
        this.store.dispatch(ActionFailed({ error }))
      }
    )
  }

  openSignEditor(contract: ContractView, signer: ContractSigner) {
    this.generateLink(contract, signer, (link) => {
      window.open(link, '_blank')
    })
  }

  onDelete(item: ContractView) {
    this.confirmDialog.confirm().subscribe(() => this.store.dispatch(DeleteContract({ id: item.id })))
  }
}
